import type { Tab, TabDefinition } from '@/components/Tabs/types.ts';
import { getUrlComposition } from '@/utils/url';
import { pickBy } from 'lodash';
import { computed, readonly, ref, Ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { TAB_PREFIX } from '../confirmationModals/useFormConfirmationModal';
import { PageTab } from '../pages/BasePage/types';

interface UseTabProps<T extends Tab | PageTab, K extends string> {
  tabs: Ref<TabDefinition<T, K> | undefined>;
  initialTab?: K;
  urlControlled?: boolean;
}

export function useTabs<T extends Tab | PageTab, K extends string>({
  tabs,
  initialTab,
  urlControlled,
}: UseTabProps<T, K>) {
  const router = useRouter();
  const route = useRoute();

  const activeIndex = computed<number | undefined>(() => {
    const index = tabKeys.value.findIndex((key) => key === activeTab.value);
    return index < 0 ? undefined : index;
  });

  const tabKeys = computed(() => (tabs.value ? (Object.keys(tabs.value) as K[]) : []));

  let initialActiveTab = initialTab;
  if (urlControlled) {
    const { key } = getUrlComposition(route.fullPath);
    initialActiveTab = key as K;
  }

  const activeTab = ref(getValidKey(initialActiveTab));

  const activeTabDefinition = computed<T | undefined>(() =>
    activeTab.value ? tabs.value?.[activeTab.value as K] : undefined,
  );

  const visibleTabs = computed(() =>
    pickBy<T>(tabs.value, (tab) => tab.hidden === undefined || !tab.hidden),
  );

  function selectIndex(index: number) {
    updateTab(tabKeys.value.at(index));
  }

  function selectByKey(key?: K) {
    updateTab(key);
  }

  function selectLast() {
    updateTab(tabKeys.value.at(-1));
  }

  function selectFirst() {
    updateTab(tabKeys.value.at(0));
  }

  function getValidKey(targetKey?: K) {
    return targetKey && tabKeys.value.includes(targetKey) ? targetKey : tabKeys.value.at(0);
  }

  function updateTab(key?: K) {
    if (urlControlled) {
      router.push({
        ...route,
        hash: `#${TAB_PREFIX}${key}`,
      });
    } else {
      activeTab.value = getValidKey(key);
    }
  }

  // This is a check if `tabs` should change and the active tab is not included any more
  watch(tabs, () => {
    if (tabs.value && activeTab.value && !Object.keys(tabs.value).includes(activeTab.value)) {
      selectByKey(getValidKey());
    }
  });

  if (urlControlled) {
    watch(
      () => route.fullPath,
      (fullPathNew) => {
        const { key } = getUrlComposition(fullPathNew);
        activeTab.value = getValidKey(key as K);
      },
    );
  }

  return {
    visibleTabs,
    activeTab: readonly(activeTab),
    activeIndex,
    activeTabDefinition,
    selectFirst,
    selectIndex,
    selectByKey,
    selectLast,
  };
}
